import React from 'react';
import { Link, useParams } from 'react-router-dom';
import V2SEO from '../components/V2SEO';
import { blogPosts } from '../../blogData';
import { business } from '../data/realContent';

// Lightweight markdown renderer for our own controlled blog content.
// Handles headings, paragraphs, unordered lists, bold/italic, and passes
// through embedded HTML blocks (img / video) untouched.
const renderMarkdown = (markdown) => {
  const lines = markdown.replace(/<!--[\s\S]*?-->/g, '').split('\n');
  const out = [];
  let paragraph = [];
  let list = [];

  const flushParagraph = () => {
    if (!paragraph.length) return;
    out.push(`<p>${inline(paragraph.join(' '))}</p>`);
    paragraph = [];
  };
  const flushList = () => {
    if (!list.length) return;
    out.push(`<ul>${list.map((li) => `<li>${inline(li)}</li>`).join('')}</ul>`);
    list = [];
  };
  const inline = (text) =>
    text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(?!\s)([^*]+?)\*/g, '<em>$1</em>');

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushParagraph();
      flushList();
    } else if (line.startsWith('## ')) {
      flushParagraph();
      flushList();
      out.push(`<h2>${inline(line.slice(3))}</h2>`);
    } else if (line.startsWith('#### ')) {
      flushParagraph();
      flushList();
      out.push(`<h3>${inline(line.slice(5))}</h3>`);
    } else if (line.startsWith('* ')) {
      flushParagraph();
      list.push(line.replace(/^\*\s+/, ''));
    } else if (line.startsWith('<')) {
      flushParagraph();
      flushList();
      out.push(line);
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushList();
  return { __html: out.join('\n') };
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <section className="v2-svc-hero">
        <div className="v2-container" style={{ textAlign: 'center' }}>
          <span className="v2-eyebrow">404</span>
          <h1>Post not found.</h1>
          <p className="v2-prose" style={{ margin: '1rem auto' }}>
            That article doesn't exist — but the rest of the blog does.
          </p>
          <Link to="/blog" className="v2-btn v2-btn--primary">Back to the blog</Link>
        </div>
      </section>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${business.url}/blog/${post.slug}`,
    },
    headline: post.title,
    description: post.excerpt,
    image: `${business.url}${post.image}`,
    author: {
      '@type': 'Person',
      name: post.authorName,
      url: `${business.url}/about`,
    },
    publisher: {
      '@type': 'Organization',
      name: business.name,
      logo: {
        '@type': 'ImageObject',
        url: `${business.url}/Cosmik%20Logo/Kolonia_Logo.png`,
      },
    },
    datePublished: post.dateISO,
    dateModified: post.dateISO,
  };

  return (
    <>
      <V2SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        ogImage={post.image}
        ogType="article"
        articleSchema={articleSchema}
      />

      <section className="v2-svc-hero" style={{ paddingBottom: '2rem' }}>
        <div className="v2-container" style={{ maxWidth: 880 }}>
          <span className="v2-eyebrow">{post.category} · {post.date}</span>
          <h1 style={{ marginTop: '0.8rem' }}>{post.title}</h1>
          <p style={{ color: 'var(--v2-text-mute)', marginTop: '1rem' }}>
            By {post.authorName}, FAA Part 107 certified drone pilot
          </p>
        </div>
      </section>

      <section className="v2-section v2-section--tight" style={{ paddingTop: 0 }}>
        <div className="v2-container" style={{ maxWidth: 880 }}>
          <div style={{
            borderRadius: '22px',
            overflow: 'hidden',
            border: '1px solid var(--v2-line)',
            aspectRatio: '16 / 9',
            marginBottom: '2.5rem',
          }}>
            <img
              src={post.image}
              alt={post.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>

          <div className="v2-article" dangerouslySetInnerHTML={renderMarkdown(post.fullContent)} />

          <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid var(--v2-line)', display: 'flex', gap: '0.7rem', flexWrap: 'wrap' }}>
            <Link to="/blog" className="v2-btn v2-btn--ghost">← All posts</Link>
            <Link to="/contact" className="v2-btn v2-btn--primary">Work with me</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogPost;
